import * as XLSX from "xlsx";
import { OrderApi, UserApi } from "../providers/Interfaces";

export const displayUserIdentifier = (record: UserApi | OrderApi): string => {
  const { employee_number: employeeNumber, email } = record;
  const uuid = "uuid" in record ? record.uuid : undefined;
  if (employeeNumber) {
    return employeeNumber;
  }
  if (email) {
    return email;
  }
  if (uuid) {
    return uuid;
  }

  return "";
};

export const convertLastWidthCategoryToSteitzWidth = (
  lwc: number
): string | undefined => {
  const lastWidthCategorySteitzMap: Map<number, "S" | "NB" | "XB" | "XXB"> =
    new Map([
      [1, "S"],
      [2, "S"],
      [3, "NB"],
      [4, "XB"],
      [5, "XXB"],
    ]);

  return lastWidthCategorySteitzMap.get(lwc);
};

export type Dictionary<T> = {
  [key: string]: T;
};

export const nestedDictHasValueForKey = (
  nestedDict: Dictionary<Dictionary<number>>,
  key: string
): boolean => Object.values(nestedDict).some((value) => value[key]);

// split text at - then capitalize string
export const splitAndCapitalize = (str: string): string => {
  return str
    .split("-")
    .map(function capitalize(part) {
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
};

export const isValidHttpUrl =
  (message: string | undefined = "Must be a URL") =>
  (str: string): string | false => {
    if (!str) return false;
    let url;
    try {
      url = new URL(str);
    } catch {
      return message;
    }

    if (url.protocol === "http:" || url.protocol === "https:") {
      return false;
    }

    return message;
  };

export const exportToExcel = (
  data: any,
  fields: string[],
  filename: string
) => {
  const filteredData = data.flatMap((item: any) => {
    const personalInfo = item.personal_info
      ? {
          first_name: item.personal_info.first_name,
          last_name: item.personal_info.last_name,
          birth_date: item.personal_info.birth_date,
        }
      : {};

    // Check if cart_items exist
    if (item.cart_items && item.cart_items.length > 0) {
      return item.cart_items.map((cartItem: any) => {
        const filteredItem: { [key: string]: any } = {};

        // Flatten top-level fields
        fields.forEach((field) => {
          if (field in item) {
            filteredItem[field] = item[field];
          }
        });

        // Add cart_items related fields to each row using dot notation
        filteredItem.pos_nr = cartItem.pos_nr;
        filteredItem.appliances_id = cartItem.appliances_id;
        filteredItem.type = cartItem.type;
        filteredItem.retailer_articlenumber = cartItem.retailer_articlenumber;
        filteredItem.product_gtin =
          cartItem.product_gtin || cartItem.additional_product_gtin;
        filteredItem.comments = cartItem.comments;
        filteredItem.mpn = cartItem.mpn;
        filteredItem.eu = cartItem.eu;

        // Merge in personal_info fields
        Object.assign(filteredItem, personalInfo);

        return filteredItem;
      });
    }

    // If no cart_items, just flatten top-level fields and personal_info
    const filteredItem: { [key: string]: any } = {};

    fields.forEach((field) => {
      if (field in item) {
        filteredItem[field] = item[field];
      }
    });

    // Merge in personal_info fields
    Object.assign(filteredItem, personalInfo);

    return filteredItem;
  });

  // Convert to Excel sheet
  const worksheet = XLSX.utils.json_to_sheet(filteredData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  // Write to file
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const exportScansDataToExcel = (
  data: any,
  fields: string[],
  filename: string
) => {
  const filteredData = data.flatMap((item: any) => {
    // Prepare customer info fields FIRST
    const customerInfo = item.customer
      ? {
          customer_email: item.customer.email,
          customer_employee_number: item.customer.employee_number,
          customer_birth_date: item.customer.birth_date,
          customer_gender: item.customer.gender,
          customer_continent: item.customer.continent,
        }
      : {};

    // Loop over each scanfile
    if (item.scanfiles && item.scanfiles.length > 0) {
      return item.scanfiles.map((scanfile: any) => {
        // Start with customer info
        const filteredItem: { [key: string]: any } = { ...customerInfo };

        // Add top-level fields (after customer info, before scanfile fields)
        fields.forEach((field) => {
          if (field in item) {
            filteredItem[field] = item[field];
          }
        });

        // Add scanfile fields
        Object.assign(filteredItem, {
          scanfile_side: scanfile.side,
          scanfile_condition: scanfile.condition,
          scanfile_mesh_url: scanfile.mesh_url,
          scanfile_colored_sole: scanfile.colored_sole,
          scanfile_real_color_plantar_image: scanfile.real_color_plantar_image,
          scanfile_plantar_image: scanfile.plantar_image,
          scanfile_plantar_analysis_visualization:
            scanfile.plantar_analysis_visualization,
          scanfile_last_processed_at: scanfile.last_processed_at,
          scanfile_status: scanfile.status,
          scanfile_processed_mesh_url: scanfile.processed_mesh_url,
          scanfile_foot_type_category: scanfile.foot_type_category,
        });

        // Flatten measurements
        if (scanfile.measurements) {
          Object.keys(scanfile.measurements).forEach((key) => {
            const value = scanfile.measurements[key];
            if (Array.isArray(value)) {
              // Convert array to a comma-separated string for Excel
              filteredItem[`measurement_${key}`] = JSON.stringify(value);
            } else if (typeof value === "number") {
              filteredItem[`measurement_${key}`] = value.toFixed(2);
            } else {
              filteredItem[`measurement_${key}`] = value;
            }
          });
        }

        // Flatten categorized_measurements
        if (scanfile.categorized_measurements) {
          Object.keys(scanfile.categorized_measurements).forEach((key) => {
            const categoryObject = scanfile.categorized_measurements[key];
            filteredItem[`categorized_${key}_value`] = categoryObject.value;
            filteredItem[`categorized_${key}_category`] =
              categoryObject.category;
          });
        }

        return filteredItem;
      });
    }

    // If no scanfiles, just return customer info and top-level fields
    const filteredItem: { [key: string]: any } = { ...customerInfo };
    fields.forEach((field) => {
      if (field in item) {
        filteredItem[field] = item[field];
      }
    });

    return filteredItem;
  });

  // Convert to Excel sheet
  const worksheet = XLSX.utils.json_to_sheet(filteredData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  // Write to file
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};
